import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';

import { DashboardBatch, DashboardAnalyticsFilters, BatchedAnalyticsResponse, ChartDataPoint } from '../../types';
import { API_CONFIG } from '../../config/api';
// Import ExcelExportService dynamically to avoid SSR issues and import conflicts
import { pdfExportService } from '../../services/PDFExportService.backend';
import { formatCurrency } from '../../utils/currency';

interface AnalyticsContextType {
  // Data
  batches: DashboardBatch[];
  chartData: ChartDataPoint[];
  overallStats: {
    totalRevenue: number;
    totalMembers: number;
    averageRevenuePerMember: number;
    memberGrowthRate: number;
    revenueGrowthRate: number;
    activityBreakdown: {
      contributions: number;
      benefits: number;
      events: number;
      expenses: number;
    };
    percentiles?: {
      revenue_50th: number;
      revenue_90th: number;
      revenue_95th: number;
      revenue_99th: number;
    };
  };
  isLoading: boolean;
  error: string | null;

  // Filters
  filters: DashboardAnalyticsFilters;
  setFilters: (filters: DashboardAnalyticsFilters) => void;

  // Real-time
  isRealTimeEnabled: boolean;
  setIsRealTimeEnabled: (enabled: boolean) => void;
  refreshInterval: number;
  setRefreshInterval: (interval: number) => void;

  // Chart interactions
  selectedMetric: string;
  setSelectedMetric: (metric: string) => void;
  selectedTimeRange: string;
  setSelectedTimeRange: (range: string) => void;

  // Drill-down
  drillDownData: any;
  setDrillDownData: (data: any) => void;
  isDrillDownActive: boolean;
  setIsDrillDownActive: (active: boolean) => void;

  // Export
  exportDashboard: (format: 'pdf' | 'excel' | 'png') => Promise<void>;

  // Performance monitoring
  performanceMetrics: {
    loadTime: number;
    renderTime: number;
    dataPoints: number;
  };

  // Utility functions
  refreshData: () => void;
}

let analyticsContext: React.Context<AnalyticsContextType | undefined> | null = null;

export function getAnalyticsContext() {
  if (!analyticsContext) {
    analyticsContext = createContext<AnalyticsContextType | undefined>(undefined);
  }
  return analyticsContext;
}

interface AnalyticsProviderProps {
  children: ReactNode;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({ children }) => {
  const [batches, setBatches] = useState<DashboardBatch[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [overallStats, setOverallStats] = useState<{
    totalRevenue: number;
    totalMembers: number;
    averageRevenuePerMember: number;
    memberGrowthRate: number;
    revenueGrowthRate: number;
    activityBreakdown: {
      contributions: number;
      benefits: number;
      events: number;
      expenses: number;
    };
    percentiles?: {
      revenue_50th: number;
      revenue_90th: number;
      revenue_95th: number;
      revenue_99th: number;
    };
  }>({
    totalRevenue: 0,
    totalMembers: 0,
    averageRevenuePerMember: 0,
    memberGrowthRate: 0,
    revenueGrowthRate: 0,
    activityBreakdown: {
      contributions: 0,
      benefits: 0,
      events: 0,
      expenses: 0
    }
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filters, setFilters] = useState<DashboardAnalyticsFilters>({
    dateRange: {
      start: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString(),
      end: new Date().toISOString()
    },
    townId: 'all',
    activityType: 'all',
    batchLevel: '1_month'
  });

  const [isRealTimeEnabled, setIsRealTimeEnabled] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const [selectedMetric, setSelectedMetric] = useState('revenue');
  const [selectedTimeRange, setSelectedTimeRange] = useState('6m');
  const [drillDownData, setDrillDownData] = useState<any>(null);
  const [isDrillDownActive, setIsDrillDownActive] = useState(false);

  const [performanceMetrics, setPerformanceMetrics] = useState({
    loadTime: 0,
    renderTime: 0,
    dataPoints: 0
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const startTime = performance.now();
    const authToken = localStorage.getItem('auth_token');

    try {
      // Check authentication
      if (!authToken) {
        throw new Error('No authentication token found. Please log in again.');
      }

      // Build query parameters
      const queryParams = new URLSearchParams();

      if (filters.dateRange) {
        queryParams.append('startDate', filters.dateRange.start);
        queryParams.append('endDate', filters.dateRange.end);
      }

      if (filters.townId && filters.townId !== 'all') {
        queryParams.append('townId', filters.townId);
      }

      if (filters.batchLevel) {
        queryParams.append('batchLevel', filters.batchLevel);
      }

      const apiUrl = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DASHBOARD}/analytics?${queryParams.toString()}`;

      console.log('AdvancedAnalyticsProvider: Fetching data with params:', {
        filters,
        queryParams: queryParams.toString(),
        apiUrl,
        tokenLength: authToken.length
      });

      // Make API call with authentication
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('AdvancedAnalyticsProvider: Response status:', response.status);

      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json() as BatchedAnalyticsResponse;

      console.log('AdvancedAnalyticsProvider: Raw API response:', result);

      if (result.success) {
        const batches = result.data?.batches || [];
        const apiOverallStats = result.data?.overallStats;
        const overallStats = {
          totalRevenue: apiOverallStats?.totalRevenue || 0,
          totalMembers: apiOverallStats?.totalMembers || 0,
          averageRevenuePerMember: apiOverallStats?.averageRevenuePerMember || 0,
          memberGrowthRate: apiOverallStats?.memberGrowthRate || 0,
          revenueGrowthRate: apiOverallStats?.revenueGrowthRate || 0,
          activityBreakdown: {
            contributions: apiOverallStats?.activityBreakdown?.contributions || 0,
            benefits: apiOverallStats?.activityBreakdown?.benefits || 0,
            events: apiOverallStats?.activityBreakdown?.events || 0,
            expenses: apiOverallStats?.activityBreakdown?.expenses || 0
          },
          percentiles: apiOverallStats?.percentiles
        };

        console.log('AdvancedAnalyticsProvider: Processing batches:', {
          batchCount: batches.length,
          batches: batches,
          overallStats: overallStats
        });

        setBatches(batches);
        const transformedChartData = transformToChartData(batches);
        console.log('AdvancedAnalyticsProvider: Transformed chart data:', {
          chartDataLength: transformedChartData.length,
          chartData: transformedChartData
        });

        setChartData(transformedChartData);
        setOverallStats(overallStats);
        setPerformanceMetrics(prev => ({
          ...prev,
          dataPoints: batches.length,
          loadTime: performance.now() - startTime
        }));

        console.log('AdvancedAnalyticsProvider: Data successfully loaded and set');
      } else {
        console.error('AdvancedAnalyticsProvider: API returned error:', result.error);
        setError(result.error || 'Failed to load analytics data');

        // Set fallback data to prevent empty state
        setBatches([]);
        setChartData([{
          time: 'No data',
          revenue: 0,
          members: 0,
          activities: 0,
          contributions: 0,
          benefits: 0,
          events: 0,
          expenses: 0
        }]);
        setOverallStats({
          totalRevenue: 0,
          totalMembers: 0,
          averageRevenuePerMember: 0,
          memberGrowthRate: 0,
          revenueGrowthRate: 0,
          activityBreakdown: {
            contributions: 0,
            benefits: 0,
            events: 0,
            expenses: 0
          }
        });
      }
    } catch (err) {
      console.error('AdvancedAnalyticsProvider: Analytics fetch error:', err);

      // Set user-friendly error message
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(`Error loading analytics data: ${errorMessage}`);

      // Set fallback data to prevent complete empty state
      setBatches([]);
      setChartData([{
        time: 'No data available',
        revenue: 0,
        members: 0,
        activities: 0,
        contributions: 0,
        benefits: 0,
        events: 0,
        expenses: 0
      }]);
      setOverallStats({
        totalRevenue: 0,
        totalMembers: 0,
        averageRevenuePerMember: 0,
        memberGrowthRate: 0,
        revenueGrowthRate: 0,
        activityBreakdown: {
          contributions: 0,
          benefits: 0,
          events: 0,
          expenses: 0
        }
      });
    } finally {
      setIsLoading(false);
      console.log('AdvancedAnalyticsProvider: Fetch operation completed');
    }
  }, [filters]);

  /**
   * Transform batched data to chart format (moved from DashboardAnalyticsService)
   */
  const transformToChartData = (batches: DashboardBatch[]): ChartDataPoint[] => {
    if (!batches || batches.length === 0) {
      // Return fallback data if no batches available
      return [{
        time: 'No data',
        revenue: 0,
        members: 0,
        activities: 0,
        contributions: 0,
        benefits: 0,
        events: 0,
        expenses: 0
      }];
    }

    try {
      const transformedData = batches.map(batch => {
        // Ensure all required properties exist with safe defaults
        return {
          time: formatTimeForChart(batch.timeRange?.start || new Date().toISOString()),
          revenue: Number(batch.summary?.totalRevenue) || 0,
          members: Number(batch.summary?.totalMembers) || 0,
          activities: Number(batch.summary?.totalActivities) || 0,
          contributions: Number(batch.activityBreakdown?.contributions) || 0,
          benefits: Number(batch.activityBreakdown?.benefits) || 0,
          events: Number(batch.activityBreakdown?.events) || 0,
          expenses: Number(batch.activityBreakdown?.expenses) || 0
        };
      }).reverse(); // Reverse to show chronological order

      console.log('AdvancedAnalyticsProvider: Successfully transformed data:', transformedData);
      return transformedData;
    } catch (error) {
      console.error('AdvancedAnalyticsProvider: Error transforming chart data:', error);
      // Return fallback data on error
      return [{
        time: 'Error loading data',
        revenue: 0,
        members: 0,
        activities: 0,
        contributions: 0,
        benefits: 0,
        events: 0,
        expenses: 0
      }];
    }
  };

  /**
   * Format time for chart display (moved from DashboardAnalyticsService)
   */
  const formatTimeForChart = (startTime: string): string => {
    const date = new Date(startTime);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isRealTimeEnabled) {
      interval = setInterval(() => {
        fetchData();
      }, refreshInterval);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isRealTimeEnabled, refreshInterval]);

  const exportDashboard = async (format: 'pdf' | 'excel' | 'png') => {
    try {
      console.log(`AdvancedAnalyticsProvider: Exporting dashboard as ${format}...`);
      console.log(`AdvancedAnalyticsProvider: Current chartData length: ${chartData.length}`);
      console.log(`AdvancedAnalyticsProvider: Current overallStats:`, overallStats);
      console.log(`AdvancedAnalyticsProvider: Current filters:`, filters);

      // Try to get WorkingExport component instance or use direct export
      const workingExportInstance = getWorkingExportInstance();

      if (workingExportInstance && workingExportInstance.handleExportForFormat) {
        console.log('AdvancedAnalyticsProvider: Using WorkingExport component for export');
        await workingExportInstance.handleExportForFormat(format);
      } else {
        console.log('AdvancedAnalyticsProvider: WorkingExport not available, using direct export');
        await performDirectExport(format, chartData, overallStats, filters);
      }

      // Also dispatch the event for any other listeners
      const exportEvent = new CustomEvent('dashboardExport', {
        detail: { format, chartData, overallStats, batches, filters }
      });

      window.dispatchEvent(exportEvent);

      console.log(`AdvancedAnalyticsProvider: Export completed for ${format}`);
    } catch (err) {
      console.error('AdvancedAnalyticsProvider: Export error:', err);
      setError('Failed to export dashboard');
      throw err; // Re-throw to allow caller to handle
    }
  };

  // Helper function to get WorkingExport instance
  const getWorkingExportInstance = (): any => {
    // Try to find WorkingExport component in the DOM or context
    // This is a fallback mechanism
    return null;
  };

  // Direct export implementation
  const performDirectExport = async (format: 'pdf' | 'excel' | 'png', chartData: ChartDataPoint[], overallStats: any, filters: DashboardAnalyticsFilters) => {
    console.log(`AdvancedAnalyticsProvider: Performing direct ${format} export`);

    switch (format) {
      case 'excel':
        await performDirectExcelExport(chartData, overallStats, filters);
        break;
      case 'pdf':
        await performDirectPDFExport(chartData, overallStats, filters);
        break;
      case 'png':
        await performDirectPNGExport(chartData, overallStats, filters);
        break;
      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  };

  // Direct Excel export implementation
  const performDirectExcelExport = async (chartData: ChartDataPoint[], overallStats: any, filters: DashboardAnalyticsFilters) => {
    try {
      console.log('AdvancedAnalyticsProvider: Starting direct Excel export...');

      // Import ExcelExportService dynamically to avoid SSR issues
      const { excelExportService } = await import('../../services/ExcelExportService');

      // Prepare professional Excel data
      const workbookData = [
        {
          'Report Type': 'Analytics Dashboard Export',
          'Generated Date': new Date().toLocaleString(),
          'Date Range': `${filters.dateRange?.start ? new Date(filters.dateRange.start).toLocaleDateString() : 'N/A'} - ${filters.dateRange?.end ? new Date(filters.dateRange.end).toLocaleDateString() : 'N/A'}`,
          'Town': filters.townId === 'all' ? 'All Towns' : (filters.townId || 'Unknown Town'),
          'Total Revenue': overallStats.totalRevenue || 0,
          'Total Members': overallStats.totalMembers || 0,
          'Average Revenue per Member': overallStats.averageRevenuePerMember || 0,
          'Member Growth Rate (%)': overallStats.memberGrowthRate || 0,
          'Revenue Growth Rate (%)': overallStats.revenueGrowthRate || 0
        },
        {
          'Activity Type': 'Contributions',
          'Count': overallStats.activityBreakdown?.contributions || 0,
          'Details': 'Member contributions to the system'
        },
        {
          'Activity Type': 'Benefits',
          'Count': overallStats.activityBreakdown?.benefits || 0,
          'Details': 'Member benefits and assistance'
        },
        {
          'Activity Type': 'Events',
          'Count': overallStats.activityBreakdown?.events || 0,
          'Details': 'Community events organized'
        },
        {
          'Activity Type': 'Expenses',
          'Count': overallStats.activityBreakdown?.expenses || 0,
          'Details': 'System operational expenses'
        }
      ];

      // Prepare detailed data
      const detailedData = chartData.map(point => ({
        'Time Period': point.time,
        'Revenue': point.revenue || 0,
        'Members': point.members || 0,
        'Activities': point.activities || 0,
        'Contributions': point.contributions || 0,
        'Benefits': point.benefits || 0,
        'Events': point.events || 0,
        'Expenses': point.expenses || 0
      }));

      // Use professional Excel service
      const filename = `Dashboard_Analytics_${new Date().toISOString().slice(0, 10)}`;
      const excelBlob = await excelExportService.generateSimpleExcel(
        [...workbookData, { '': '' }, ...detailedData],
        filename,
        'Dashboard Analytics'
      );

      // Trigger download
      const url = URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${filename}.xlsx`;
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('AdvancedAnalyticsProvider: Direct Excel export completed');
    } catch (error) {
      console.error('AdvancedAnalyticsProvider: Direct Excel export failed:', error);
      throw error;
    }
  };

  // Direct PDF export implementation
  const performDirectPDFExport = async (chartData: ChartDataPoint[], overallStats: any, filters: DashboardAnalyticsFilters) => {
    try {
      console.log('AdvancedAnalyticsProvider: Starting direct PDF export...');

      // Prepare chart data for PDF
      const chartDataForPDF = chartData.map(point => ({
        time: point.time,
        revenue: point.revenue || 0,
        members: point.members || 0,
        activities: point.activities || 0,
        contributions: point.contributions || 0,
        benefits: point.benefits || 0,
        events: point.events || 0,
        expenses: point.expenses || 0
      }));

      // Generate PDF using the professional service - use the correct method that returns a Blob
      let pdfBlob = await pdfExportService.generateReportWithChartsData(
        chartDataForPDF,
        chartDataForPDF,
        'financial',
        {
          header: {
            title: 'Dashboard Analytics Report',
            dateRange: `${filters.dateRange?.start ? new Date(filters.dateRange.start).toLocaleDateString() : 'N/A'} - ${filters.dateRange?.end ? new Date(filters.dateRange.end).toLocaleDateString() : 'N/A'}`,
            townName: filters.townId === 'all' ? 'All Towns' : (filters.townId || 'Unknown Town'),
            generatedDate: new Date().toLocaleDateString()
          },
          styles: {
            colors: {
              primary: '#2563eb',
              secondary: '#1e40af',
              text: '#1f2937',
              background: '#ffffff',
              accent: '#f3f4f6'
            },
            fonts: {
              header: 'Roboto',
              body: 'Roboto',
              monospace: 'Courier'
            }
          },
          layout: {
            margins: { top: 40, right: 40, bottom: 40, left: 40 },
            columns: 12,
            pageOrientation: 'portrait'
          }
        }
      );

      // Check if the result is already a Blob, if not, we need to generate it properly
      if (pdfBlob instanceof Blob) {
        console.log('AdvancedAnalyticsProvider: PDF Blob received successfully');
      } else {
        console.log('AdvancedAnalyticsProvider: PDF data structure received, need to generate actual PDF');
        // For now, let's create a simple PDF content as fallback
        const jsPDF = (await import('jspdf')).default;
        const doc = new jsPDF();

        // Add content to the PDF
        doc.setFontSize(16);
        doc.text('Dashboard Analytics Report', 20, 20);
        doc.setFontSize(12);
        doc.text(`Generated: ${new Date().toLocaleString()}`, 20, 30);
        doc.text(`Town: ${filters.townId === 'all' ? 'All Towns' : (filters.townId || 'Unknown Town')}`, 20, 40);

        // Add summary
        doc.setFontSize(14);
        doc.text('Summary', 20, 50);
        doc.setFontSize(12);
        doc.text(`Total Revenue: ${formatCurrency(overallStats.totalRevenue || 0)}`, 20, 60);
        doc.text(`Total Members: ${(overallStats.totalMembers || 0).toLocaleString()}`, 20, 70);

        // Convert to Blob
        const pdfContent = doc.output('blob');
        pdfBlob = pdfContent;
      }

      // Trigger download
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Dashboard_Analytics_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.style.visibility = 'hidden';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('AdvancedAnalyticsProvider: Direct PDF export completed');
    } catch (error) {
      console.error('AdvancedAnalyticsProvider: Direct PDF export failed:', error);
      throw error;
    }
  };

  // Direct PNG export implementation
  const performDirectPNGExport = async (chartData: ChartDataPoint[], overallStats: any, filters: DashboardAnalyticsFilters) => {
    try {
      console.log('AdvancedAnalyticsProvider: Starting direct PNG export...');

      // Create a comprehensive dashboard screenshot
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas context not available');

      // High-quality canvas
      const scale = 2;
      canvas.width = 1000 * scale;
      canvas.height = 700 * scale;
      ctx.scale(scale, scale);

      // Background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Header
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 28px Arial';
      ctx.fillText('Ghana Town Membership System', 20, 40);
      ctx.font = 'bold 20px Arial';
      ctx.fillText('Dashboard Analytics Report', 20, 70);

      // Date and filters
      ctx.font = '14px Arial';
      ctx.fillStyle = '#6b7280';
      ctx.fillText(`Generated: ${new Date().toLocaleString()}`, 20, 100);
      ctx.fillText(`Town: ${filters.townId === 'all' ? 'All Towns' : filters.townId}`, 20, 120);

      // Summary statistics
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#1f2937';
      ctx.fillText('Key Metrics', 20, 160);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#374151';
      const metrics = [
        `Total Revenue: ${formatCurrency(overallStats.totalRevenue || 0)}`,
        `Total Members: ${(overallStats.totalMembers || 0).toLocaleString()}`,
        `Avg Revenue/Member: ${formatCurrency(overallStats.averageRevenuePerMember || 0)}`,
        `Member Growth: ${overallStats.memberGrowthRate?.toFixed(2) || '0'}%`,
        `Revenue Growth: ${overallStats.revenueGrowthRate?.toFixed(2) || '0'}%`,
        `Total Activities: ${(overallStats.activityBreakdown?.contributions || 0) + (overallStats.activityBreakdown?.benefits || 0) + (overallStats.activityBreakdown?.events || 0) + (overallStats.activityBreakdown?.expenses || 0)}`
      ];

      metrics.forEach((metric, index) => {
        ctx.fillText(metric, 20, 190 + (index * 25));
      });

      // Activity breakdown
      ctx.font = 'bold 18px Arial';
      ctx.fillStyle = '#1f2937';
      ctx.fillText('Activity Breakdown', 500, 160);

      ctx.font = '14px Arial';
      ctx.fillStyle = '#374151';
      const activities = [
        `Contributions: ${overallStats.activityBreakdown?.contributions || 0}`,
        `Benefits: ${overallStats.activityBreakdown?.benefits || 0}`,
        `Events: ${overallStats.activityBreakdown?.events || 0}`,
        `Expenses: ${overallStats.activityBreakdown?.expenses || 0}`
      ];

      activities.forEach((activity, index) => {
        ctx.fillText(activity, 500, 190 + (index * 25));
      });

      // Revenue trend chart
      if (chartData.length > 0) {
        ctx.font = 'bold 16px Arial';
        ctx.fillStyle = '#1f2937';
        ctx.fillText('Revenue Trend (Last 10 periods)', 20, 380);

        // Chart area
        const chartX = 20;
        const chartY = 400;
        const chartWidth = 460;
        const chartHeight = 200;

        // Chart background
        ctx.strokeStyle = '#e5e7eb';
        ctx.strokeRect(chartX, chartY, chartWidth, chartHeight);

        // Grid lines
        ctx.strokeStyle = '#f3f4f6';
        for (let i = 1; i < 5; i++) {
          const y = chartY + (i * chartHeight / 5);
          ctx.beginPath();
          ctx.moveTo(chartX, y);
          ctx.lineTo(chartX + chartWidth, y);
          ctx.stroke();
        }

        // Bars
        const maxRevenue = Math.max(...chartData.slice(0, 10).map(d => d.revenue || 0));
        const barWidth = chartWidth / Math.min(chartData.length, 10) - 4;

        chartData.slice(0, 10).forEach((point, index) => {
          const barHeight = maxRevenue > 0 ? (point.revenue || 0) / maxRevenue * (chartHeight - 20) : 0;
          const x = chartX + 2 + (index * (barWidth + 4));
          const y = chartY + chartHeight - barHeight - 10;

          // Gradient bar
          const gradient = ctx.createLinearGradient(x, y, x, y + barHeight);
          gradient.addColorStop(0, '#3b82f6');
          gradient.addColorStop(1, '#1d4ed8');
          ctx.fillStyle = gradient;
          ctx.fillRect(x, y, barWidth, barHeight);
        });

        // Y-axis labels
        ctx.font = '12px Arial';
        ctx.fillStyle = '#6b7280';
        ctx.textAlign = 'right';
        for (let i = 0; i <= 5; i++) {
          const value = (maxRevenue * i / 5).toLocaleString();
          const y = chartY + chartHeight - (i * chartHeight / 5);
          ctx.fillText(`${formatCurrency(Number(value.replace(/,/g, '')))}`, chartX - 5, y + 4);
        }
        ctx.textAlign = 'left';
      }

      // Footer
      ctx.font = '12px Arial';
      ctx.fillStyle = '#9ca3af';
      ctx.fillText('Generated by Ghana Town Membership System', 20, 650);

      // Download
      const link = document.createElement('a');
      link.download = `Dashboard_Analytics_${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      console.log('AdvancedAnalyticsProvider: Direct PNG export completed');
    } catch (error) {
      console.error('AdvancedAnalyticsProvider: Direct PNG export failed:', error);
      throw error;
    }
  };

  // Add a manual refresh function
  const refreshData = useCallback(() => {
    console.log('AdvancedAnalyticsProvider: Manually refreshing data...');
    fetchData();
  }, [fetchData]);

  const contextValue: AnalyticsContextType = {
    // Data
    batches,
    chartData,
    overallStats,
    isLoading,
    error,

    // Filters
    filters,
    setFilters,

    // Real-time
    isRealTimeEnabled,
    setIsRealTimeEnabled,
    refreshInterval,
    setRefreshInterval,

    // Chart interactions
    selectedMetric,
    setSelectedMetric,
    selectedTimeRange,
    setSelectedTimeRange,

    // Drill-down
    drillDownData,
    setDrillDownData,
    isDrillDownActive,
    setIsDrillDownActive,

    // Export
    exportDashboard,

    // Performance monitoring
    performanceMetrics,

    // Utility functions
    refreshData
  };

  const AnalyticsContext = getAnalyticsContext();

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export const useAnalytics = () => {
  const context = useContext(getAnalyticsContext());
  if (context === undefined) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};
